import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Tab,
  Tabs,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import CircleNotificationsIcon from "@mui/icons-material/CircleNotifications";
import CloseIcon from "@mui/icons-material/Close";
import GenericBreadCrums from "@components/Breadcrumbs";

const member = {
  name: "Rajesh Kumar",
  initials: "RK",
  firstName: "Rajesh",
  lastName: "Kumar",
  designation: "Senior Officer",
  status: "Active",
  assignedRole: "Nodal Officer",
  spocId: "SPOC-2024-0042",
  email: "rajesh.kumar@example.gov.in",
  phone: "+91 98765 43210",
  organisation: "Ministry of Urban Development",
  onboardedOn: "12 Jan 2024",
  onboardedDate: "January 12, 2024",
};

interface InfoFieldProps {
  label: string;
  value: string;
  isEmail?: boolean;
  locked?: boolean;
}

const InfoField: React.FC<InfoFieldProps> = ({ label, value, isEmail, locked }) => (
  <Box>
    <Typography sx={{ fontSize: "12px", color: "#757575", mb: 0.5 }}>
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      {isEmail && <EmailOutlinedIcon sx={{ fontSize: 15, color: "#9E9E9E" }} />}
      <Typography sx={{ fontSize: "14px", color: "#212121", fontWeight: 500 }}>
        {value}
      </Typography>
      {locked && (
        <LockOutlinedIcon sx={{ fontSize: 14, color: "#BDBDBD", ml: 0.5 }} />
      )}
    </Box>
  </Box>
);

const renderStatusBadge = (status: string) => {
  const statusStyles: Record<string, { text: string; bg: string }> = {
    Active: { text: "#2E7D32", bg: "#DBEFDC" },
    Inactive: { text: "#757575", bg: "#ECECEC" },
    "In active": { text: "#757575", bg: "#ECECEC" },
    Deactivated: { text: "#B3261D", bg: "#F0D4D2" },
    Pending: { text: "#9F7803", bg: "#FFF3CD" },
  };
  const defaultStyle = { text: "#374151", bg: "#f3f4f6" };
  const style = statusStyles[status] || defaultStyle;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.5,
        py: 0.5,
        bgcolor: style.bg,
        color: style.text,
        padding: "4px 10px",
        borderRadius: "6px",
        fontSize: "12px",
        fontWeight: 600,
      }}
    >
      <Box
        component="span"
        sx={{
          display: "inline-block",
          width: 4,
          height: 4,
          borderRadius: "50%",
          bgcolor: style.text,
        }}
      />
      {status}
    </Box>
  );
};

export const EditDetailsMember: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: "Sophie",
    lastName: "Kim",
    designation: "Highway Engineer",
    assignedRole: "Checker",
    email: "sophie.kim@techcorp.com",
    phone: "+91-91234-56789",
  });

  const [selectedRole, setSelectedRole] = useState("Checker");
  const roleOptions = ["Viewer", "Maker", "Checker", "Approver"];

  const breadcrumbData = [
    { label: "SPOC Members", link: "/spoc-members" },
    { label: "Edit Details", link: "#" },
  ];

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRoleChange = (role: string) => {
    setSelectedRole(role);
    setFormData((prev) => ({
      ...prev,
      assignedRole: role,
    }));
  };

  const handleSaveChanges = () => {
    console.log("Saving changes:", formData);
    // Add API call here
  };

  const handleCancel = () => {
    console.log("Cancelled");
    // Navigate back or reset form
  };

  return (
    <Box className="app-details-wrapper login-root">
      <div className="page-content">
        <GenericBreadCrums breadcrumbs={breadcrumbData} className="mt-6" />

        <Box className="app-details-card">
          {/* Header Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              bgcolor: "#F5F0FF",
              borderRadius: "12px",
              mb: 3,
            }}
          >
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: "#3A1C73",
                color: "#fff",
                fontWeight: 700,
                fontSize: "20px",
              }}
            >
              {formData.firstName.charAt(0)}{formData.lastName.charAt(0)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#212121" }}>
                {formData.firstName} {formData.lastName}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <Typography sx={{ fontSize: "13px", color: "#757575" }}>
                  ID: SPOC-00124
                </Typography>
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#2E7D32",
                    fontWeight: 600,
                  }}
                >
                  Active
                </Typography>
              </Box>
            </Box>
          </Box>

          <Card
            className="app-main-content"
            sx={{ borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}
          >
            <CardContent sx={{ p: 3 }}>
              {/* Basic Information Section */}
              <Typography
                sx={{ fontWeight: 700, fontSize: "16px", color: "#212121", mb: 2.5 }}
              >
                Basic Information
              </Typography>

              <Grid container spacing={3} sx={{ mb: 3.5 }}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#212121",
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    First Name <span style={{ color: "#B3261D" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        bgcolor: "#FAFAFA",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#212121",
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Last Name <span style={{ color: "#B3261D" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        bgcolor: "#FAFAFA",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#212121",
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Designation <span style={{ color: "#B3261D" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        bgcolor: "#FAFAFA",
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Role Assignment Section */}
              <Typography
                sx={{ fontWeight: 700, fontSize: "16px", color: "#212121", mb: 2.5 }}
              >
                Role Assignment
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#212121",
                    fontWeight: 600,
                    mb: 1.5,
                  }}
                >
                  Assigned Role <span style={{ color: "#B3261D" }}>*</span>
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
                  <Select
                    name="assignedRole"
                    value={selectedRole}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    sx={{
                      borderRadius: "8px",
                      bgcolor: "#FAFAFA",
                    }}
                  >
                    {roleOptions.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Role Pills */}
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  {roleOptions.map((role) => (
                    <Chip
                      key={role}
                      label={role}
                      onClick={() => handleRoleChange(role)}
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        height: "32px",
                        cursor: "pointer",
                        bgcolor: selectedRole === role ? "#3A1C73" : "#F5F0FF",
                        color: selectedRole === role ? "#fff" : "#3A1C73",
                        border: selectedRole === role ? "none" : "none",
                        "&:hover": {
                          bgcolor: selectedRole === role ? "#4A2D7A" : "#EDE7F6",
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Contact Details Section */}
              <Typography
                sx={{ fontWeight: 700, fontSize: "16px", color: "#212121", mb: 0.5 }}
              >
                Contact Details{" "}
                <span style={{ fontSize: "13px", fontWeight: 500, color: "#757575" }}>
                  (Aadhaar-linked — non-editable)
                </span>
              </Typography>

              <Grid container spacing={3} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#212121",
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Email ID
                  </Typography>
                  <TextField
                    fullWidth
                    name="email"
                    value={formData.email}
                    disabled
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        bgcolor: "#F5F5F5",
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#9E9E9E",
                      mt: 0.75,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    🔒 Cannot be changed
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#212121",
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Mobile Number
                  </Typography>
                  <TextField
                    fullWidth
                    name="phone"
                    value={formData.phone}
                    disabled
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        bgcolor: "#F5F5F5",
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#9E9E9E",
                      mt: 0.75,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    🔒 Cannot be changed
                  </Typography>
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  mt: 4,
                  pt: 2,
                  borderTop: "1px solid #E0E0E0",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    px: 3,
                    py: 1,
                    borderRadius: "50px",
                    borderColor: "#3A1C73",
                    color: "#3A1C73",
                    "&:hover": {
                      borderColor: "#3A1C73",
                      bgcolor: "#F5F0FF",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveChanges}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    px: 3,
                    py: 1,
                    borderRadius: "50px",
                    bgcolor: "#3A1C73",
                    "&:hover": {
                      bgcolor: "#4A2D7A",
                    },
                  }}
                >
                  Save Changes
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </div>
    </Box>
  );
};
